import styled, { css } from '../theme/styled';
import type { V4Theme } from '../theme';
import { Counter } from '../counter';
import { StyledButtonProps } from './button.types';
import { buttonColorsMap } from './button.colors';
import { active, counterStyles } from './styled-mixins';

const sizeMap = (theme: V4Theme) =>
  ({
    l: {
      paddingInline: theme.spacings.base,
      height: '48px',
      borderRadius: theme.radiuses.xs,
    },
    m: {
      paddingInline: theme.spacings.base,
      height: '40px',
      borderRadius: theme.radiuses.xs,
    },
    s: {
      paddingInline: theme.spacings.s,
      height: '32px',
      borderRadius: theme.radiuses.xxs,
    },
    xs: {
      paddingInline: theme.spacings.s,
      height: '24px',
      borderRadius: theme.radiuses.xxs,
    },
  } as const);

export const StyledButton = styled.button<StyledButtonProps>`
  ${({ theme, $size, $mode, $variant, $isIconButton }) => {
    const { paddingInline, height, borderRadius } = sizeMap(theme)[$size];

    return css`
      box-sizing: border-box;
      padding-inline: ${paddingInline};
      position: relative;
      padding: 0;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: fit-content;
      height: ${height};
      max-width: 100%;
      background: none;
      border: 1px solid transparent;
      border-radius: ${borderRadius};
      outline: none;
      transition-property: box-shadow, transform, background-color, color,
        border-color;
      transition-duration: ${theme.tokens.animation.duration.base};
      cursor: pointer;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;

      ${$size === 'l'
        ? theme.fonts['button-16'].css
        : theme.fonts['button-14'].css}

      ${active(css`
        transform: scale(0.96);
      `)}

      ${sizeMap(theme)[$size]};
      ${buttonColorsMap(theme.colors)[$mode][$variant]};

      ${$isIconButton &&
      css`
        padding-inline: 0;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: ${height};
      `}

      &:disabled {
        background-color: ${theme.colors.gray[100]};
        color: ${theme.colors.gray[400]};
        border-color: transparent;
        cursor: not-allowed;

        ${counterStyles({
          color: theme.colors.gray[100],
          bg: theme.colors.gray[400],
        })}
      }
    `;
  }}
`;

export const PendingWrapper = styled.span`
  ${({ theme }) => css`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: inherit;
    transition-duration: ${theme.tokens.animation.duration.base};
    animation: fadeIn ${theme.tokens.animation.duration.base} ease forwards;

    @keyframes fadeIn {
      from {
        opacity: 0;
        transform: translateY(-100%);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
  `}
`;

export const ContentWrapper = styled.span<{ $pending?: boolean }>`
  ${({ theme, $pending }) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: ${theme.spacings.xs};
    width: 100%;
    transform: translateY(${$pending ? '100%' : '0'});
    opacity: ${$pending ? '0' : '1'};
    transition-duration: ${theme.tokens.animation.duration.base};
    transition-property: transform, opacity;
    transition-timing-function: ease;
  `}
`;

export const IconWrapper = styled.span`
  min-width: 16px;
  min-height: 16px;
`;

export const StyledCounter = styled(Counter)`
  background-color: var(--counter-bg);
  color: var(--counter-color);
`;
